import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Briefcase, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

import { API_BASE } from '../config';

// const API_BASE = "http://127.0.0.1:8000";


export default function AddToJobModal({ candidate, onClose }) {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedJob, setSelectedJob] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchJobs = async () => {
            try {
                const res = await axios.get(`${API_BASE}/jobs`);
                // Only show open jobs
                const openJobs = res.data.filter(j => j.status === 'open');
                setJobs(openJobs);
            } catch (err) {
                console.error("Failed to load jobs", err);
                toast.error("Could not load active jobs");
            } finally {
                setLoading(false);
            }
        };
        fetchJobs();
    }, []);

    const handleApply = async () => {
        if (!selectedJob) return;
        setSubmitting(true);
        try {
            await axios.post(`${API_BASE}/jobs/${selectedJob}/apply`, {
                job_id: selectedJob,
                resume_id: candidate._id || candidate.resume_id,
                status: 'applied'
            });
            toast.success("Candidate linked to job!");
            onClose();
        } catch (err) {
            console.error(err);
            if (err.response && err.response.status === 400) {
                // Usually means "Job ID mismatch", but here we might flag dupes if we had better error handling
                toast.error("Checking... Candidate might already be added.");
            } else {
                toast.error("Failed to link candidate");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.95, y: 20 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0.95, y: 20 }}
                    className="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl shadow-xl overflow-hidden"
                    onClick={e => e.stopPropagation()}
                >
                    <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
                        <div>
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white">Add to Job</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Link <strong>{candidate.name}</strong> to a pipeline</p>
                        </div>
                        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
                    </div>

                    <div className="p-5 max-h-[60vh] overflow-y-auto space-y-3">
                        {loading && <div className="text-center py-4 text-slate-500">Loading jobs...</div>}

                        {!loading && jobs.length === 0 && (
                            <div className="text-center py-4 text-slate-500">No active jobs found. Create one first!</div>
                        )}

                        {jobs.map(job => (
                            <div
                                key={job._id}
                                onClick={() => setSelectedJob(job._id)}
                                className={`p-4 rounded-xl border cursor-pointer transition-all flex items-center justify-between ${selectedJob === job._id
                                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 dark:border-brand-500'
                                    : 'border-slate-200 dark:border-slate-700 hover:border-brand-300 dark:hover:border-slate-600'
                                    }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg ${selectedJob === job._id ? 'bg-brand-100 text-brand-600' : 'bg-slate-100 text-slate-500 dark:bg-slate-800'
                                        }`}>
                                        <Briefcase className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h4 className={`font-semibold text-sm ${selectedJob === job._id ? 'text-brand-700 dark:text-brand-300' : 'text-slate-700 dark:text-slate-200'}`}>
                                            {job.title}
                                        </h4>
                                        <p className="text-xs text-slate-500">{job.department} • {job.location}</p>
                                    </div>
                                </div>
                                {selectedJob === job._id && <Check className="w-5 h-5 text-brand-600" />}
                            </div>
                        ))}
                    </div>

                    <div className="p-5 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 bg-slate-50 dark:bg-slate-900/50">
                        <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium text-sm">Cancel</button>
                        <button
                            onClick={handleApply}
                            disabled={!selectedJob || submitting}
                            className="px-6 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded-lg font-bold text-sm shadow-lg shadow-brand-200 dark:shadow-none transition-all"
                        >
                            {submitting ? 'Linking...' : 'Confirm & Add'}
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
